import type {
  DashboardScope,
  DashboardScopeType,
  DashboardWidgetName,
} from '@/core/interfaces/Dashboard';

/** Frontend stale times (ms) per widget — matches contract §21 */
export const DASHBOARD_STALE_TIME_MS: Record<DashboardWidgetName, number> = {
  meta: 30 * 60 * 1000,
  summary: 5 * 60 * 1000,
  'system-summary': 5 * 60 * 1000,
  map: 10 * 60 * 1000,
  'inventory-movement': 5 * 60 * 1000,
  'ticket-statuses': 2 * 60 * 1000,
  alerts: 2 * 60 * 1000,
  'top-entities': 5 * 60 * 1000,
  'recent-activities': 30 * 1000,
  'cache-versions': 30 * 1000,
};

export const DASHBOARD_VERSION_POLL_INTERVAL_MS = 30_000;

export const DASHBOARD_SKIP_CACHE =
  process.env.VUE_APP_DASHBOARD_SKIP_CACHE === 'true';

export const DASHBOARD_DEFAULT_MAP_CENTER = {
  latitude: 16.047079,
  longitude: 108.20623,
  lat: 16.047079,
  lng: 108.20623,
};

export const DASHBOARD_DEFAULT_MAP_ZOOM = 5;

export const DASHBOARD_QUERY_ROOT_KEY = 'dashboard' as const;

/** Widgets invalidated when unit version changes */
export const DASHBOARD_UNIT_SCOPED_WIDGETS: DashboardWidgetName[] = [
  'summary',
  'system-summary',
  'inventory-movement',
  'ticket-statuses',
  'alerts',
  'top-entities',
];

export const DASHBOARD_MARKER_STATUS_COLORS: Record<string, string> = {
  healthy: '#23B95F',
  warning: '#FF9800',
  critical: '#FF3B30',
  inactive: '#8A97A8',
};

/** Light-theme map panel background */
export const DASHBOARD_MAP_SEA_COLOR = '#EAF7FF';
export const DASHBOARD_MAP_PANEL_BG = '#f8fbff';

export const DASHBOARD_KPI_LABELS: Record<string, string> = {
  ending_amount: 'Giá trị tồn kho',
  ending_quantity: 'Số lượng tồn',
  material_count: 'Số vật tư',
  warehouse_count: 'Số kho',
  import_amount: 'Giá trị nhập',
  export_amount: 'Giá trị xuất',
  pending_ticket_count: 'Phiếu chờ duyệt',
  alert_count: 'Cảnh báo',
  negative_stock_count: 'Tồn âm',
};

export const FINANCIAL_KPI_KEYS = new Set([
  'ending_amount',
  'import_amount',
  'export_amount',
]);

/** System summary grid — 3-column layout (excludes sync / last update / negative stock). */
export const DASHBOARD_SYSTEM_SUMMARY_KEYS = [
  'unit_count',
  'warehouse_count',
  'inventory_amount',
  'pending_count',
  'alert_count',
  'queue_error_count',
] as const;

export const DASHBOARD_SEVERITY_VALUE_COLORS: Record<string, string> = {
  info: '#3b82f6',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  default: '#181c32',
};

export const DASHBOARD_ALERT_SEVERITY_STYLES: Record<
  string,
  { bg: string; tone: string }
> = {
  critical: { bg: '#fef2f2', tone: '#ef4444' },
  warning: { bg: '#fff7ed', tone: '#f59e0b' },
  info: { bg: '#eff6ff', tone: '#3b82f6' },
};

export const DASHBOARD_ACTIVITY_SEVERITY_COLORS: Record<string, string> = {
  success: '#22c55e',
  info: '#3b82f6',
  default: '#3b82f6',
  warning: '#f59e0b',
  danger: '#ef4444',
};

export function formatAlertListLabel(count: number, label: string): string {
  const trimmed = label.trim();
  if (trimmed.startsWith(String(count))) {
    return trimmed;
  }
  return `${count} ${trimmed}`;
}

export function getDefaultTopEntityFilters(scope: DashboardScope | null): {
  entityType: 'unit' | 'warehouse' | 'material';
  metric: 'ending_amount' | 'import_amount' | 'export_amount' | 'alert_count';
} {
  if (scope?.type === 'warehouse') {
    return { entityType: 'material', metric: 'ending_amount' };
  }
  if (scope?.type === 'unit' || scope?.type === 'province') {
    return { entityType: 'warehouse', metric: 'ending_amount' };
  }
  return { entityType: 'unit', metric: 'ending_amount' };
}

export function getDefaultTopEntityFiltersForScopeType(
  scopeType: DashboardScopeType | null
): {
  entityType: 'unit' | 'warehouse' | 'material';
  metric: 'ending_amount' | 'import_amount' | 'export_amount' | 'alert_count';
} {
  if (scopeType === 'warehouse') {
    return { entityType: 'material', metric: 'ending_amount' };
  }
  if (scopeType === 'unit' || scopeType === 'province') {
    return { entityType: 'warehouse', metric: 'ending_amount' };
  }
  return { entityType: 'unit', metric: 'ending_amount' };
}
